import React from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { selection } from '@/lib/utils/haptics'
import { SlidersHorizontal } from '@/components/ui/icons'
import {
  AllCarsIcon,
  ElectricVehicleIcon,
  FunVehicleIcon,
  MicrobusIcon,
  MotorcycleVehicleIcon,
  PickupIcon,
  PremiumVehicleIcon,
  SedanIcon,
  SuvVehicleIcon,
  WorkVehicleIcon,
} from '@/components/icons/VehicleCategoryIcons'
import { useTranslation } from 'react-i18next'
import { Pressable } from '../ui/Pressable'
import { GlassSurface } from '../ui/GlassSurface'
import { useSemanticColors, RADIUS, SPACING } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { COLORS, FONTS } from '../../lib/brand'
import type { VehicleCategory } from '../../lib/types/vehicle.types'

type IconComponent = React.ComponentType<{
  size?: number
  color?: string
  strokeWidth?: number
  fill?: string
}>

const CATEGORY_CHIP_HEIGHT = 46
const CATEGORY_MOBILE_CHIP_HEIGHT = 50
const CATEGORY_ICON_SIZE = 22
const categoryRipple = { color: COLORS.background.cardBorder, borderless: false } as const
const ACTIVE_CATEGORY_COLOR = COLORS.purple.DEFAULT
const CATEGORY_BRAND_PURPLE = COLORS.purple.DEFAULT
const SOLID_AMBER = '#F9A825'
const SOLID_ELECTRIC = '#16C784'
const SOLID_SKY = '#2F6FED'
const SOLID_TEAL = '#0F9F8F'
const SOLID_VIOLET = '#7C3AED'
const SOLID_SLATE = '#475569'
const SOLID_ORANGE = '#F97316'

const CATEGORIES: {
  id: VehicleCategory
  i18nKey: string
  icon: IconComponent
  accentColor: string
}[] = [
  {
    id: 'luxury',
    i18nKey: 'explore.categories.luxury',
    icon: PremiumVehicleIcon,
    accentColor: SOLID_AMBER,
  },
  {
    id: 'sports',
    i18nKey: 'explore.categories.sports',
    icon: ElectricVehicleIcon,
    accentColor: SOLID_ELECTRIC,
  },
  {
    id: 'suv',
    i18nKey: 'explore.categories.suv',
    icon: SuvVehicleIcon,
    accentColor: CATEGORY_BRAND_PURPLE,
  },
  {
    id: 'sedan',
    i18nKey: 'explore.categories.sedan',
    icon: SedanIcon,
    accentColor: ACTIVE_CATEGORY_COLOR,
  },
  {
    id: 'pickup',
    i18nKey: 'explore.categories.pickup',
    icon: PickupIcon,
    accentColor: SOLID_SKY,
  },
  {
    id: 'microbus',
    i18nKey: 'explore.categories.microbus',
    icon: MicrobusIcon,
    accentColor: SOLID_TEAL,
  },
  {
    id: 'recreational',
    i18nKey: 'explore.categories.recreational',
    icon: FunVehicleIcon,
    accentColor: SOLID_VIOLET,
  },
  {
    id: 'heavy_duty',
    i18nKey: 'explore.categories.heavy_duty',
    icon: WorkVehicleIcon,
    accentColor: SOLID_SLATE,
  },
  {
    id: 'motorcycle',
    i18nKey: 'explore.categories.motorcycle',
    icon: MotorcycleVehicleIcon,
    accentColor: SOLID_ORANGE,
  },
]

type CategoryGlyphProps = {
  IconComponent: IconComponent
  active: boolean
  accentColor: string
  compact?: boolean
}

function CategoryGlyph({ IconComponent, active, accentColor, compact }: CategoryGlyphProps) {
  const frameSize = compact ? 23 : 26
  const iconSize = compact ? 20 : CATEGORY_ICON_SIZE
  const iconColor = active ? '#FFFFFF' : accentColor

  return (
    <View
      style={{
        width: frameSize,
        height: frameSize,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconComponent
        size={iconSize}
        color={iconColor}
        strokeWidth={compact ? 2.05 : 2.15}
        fill="none"
      />
    </View>
  )
}

interface CategoryFilterProps {
  selected: VehicleCategory | null
  onSelect: (category: VehicleCategory | null) => void
  desktop?: boolean
  contentPadding?: number
  onFilterPress?: () => void
  activeFilterCount?: number
}

export function CategoryFilter({
  selected,
  onSelect,
  desktop,
  contentPadding = SPACING.lg,
  onFilterPress,
  activeFilterCount = 0,
}: CategoryFilterProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const allActive = selected === null

  function renderChip({
    id,
    label,
    IconComponent,
    active,
    accentColor,
    onPress,
    testID,
    badge,
  }: {
    id: string
    label: string
    IconComponent: IconComponent
    active: boolean
    accentColor: string
    onPress: () => void
    testID: string
    badge?: number
  }) {
    const chipHeight = desktop ? CATEGORY_CHIP_HEIGHT : CATEGORY_MOBILE_CHIP_HEIGHT
    const activeBackground = ACTIVE_CATEGORY_COLOR
    const labelColor = active ? '#FFFFFF' : colors.textPrimary
    const chipContentLayout = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      gap: 8,
      paddingHorizontal: desktop ? 14 : 13,
    }
    const chipLayout = { minHeight: chipHeight, ...chipContentLayout }
    const chipContent = (
      <>
        <CategoryGlyph
          IconComponent={IconComponent}
          active={active}
          accentColor={active ? '#FFFFFF' : accentColor}
          compact={desktop}
        />
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.88}
          style={{
            fontSize: desktop ? 13 : 14,
            lineHeight: desktop ? 18 : 19,
            fontFamily: active ? FONTS.bodyBold : FONTS.body,
            color: labelColor,
          }}
        >
          {label}
        </Text>
        {badge ? (
          <View
            testID="explore.active-filters"
            style={{
              minWidth: 20,
              height: 20,
              borderRadius: 10,
              backgroundColor: active ? '#FFFFFF' : COLORS.coral.DEFAULT,
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: 6,
            }}
          >
            <Text
              numberOfLines={1}
              adjustsFontSizeToFit
              style={{
                fontSize: 12,
                fontFamily: FONTS.bodyBold,
                color: active ? ACTIVE_CATEGORY_COLOR : '#FFFFFF',
              }}
            >
              {badge}
            </Text>
          </View>
        ) : null}
      </>
    )

    return (
      <Pressable
        key={id}
        onPress={onPress}
        android_ripple={categoryRipple}
        style={({ pressed }) => ({
          borderRadius: RADIUS.lg,
          overflow: 'visible',
          opacity: pressed ? 0.9 : 1,
        })}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        accessibilityLabel={label}
        testID={testID}
      >
        {active ? (
          <View
            style={{
              ...chipLayout,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: activeBackground,
              backgroundColor: activeBackground,
            }}
          >
            {chipContent}
          </View>
        ) : (
          <GlassSurface
            tone="quiet"
            radius={RADIUS.lg}
            style={{ minHeight: chipHeight }}
            contentStyle={chipContentLayout}
          >
            {chipContent}
          </GlassSurface>
        )}
      </Pressable>
    )
  }

  const showAllItem = renderChip({
    id: 'all',
    label: t('explore.show_all'),
    IconComponent: AllCarsIcon,
    active: allActive,
    accentColor: CATEGORY_BRAND_PURPLE,
    onPress: () => {
      void selection()
      onSelect(null)
    },
    testID: 'explore.category-all',
  })

  const items = CATEGORIES.map((cat) => {
    const active = selected === cat.id
    const IconComponent = cat.icon
    return renderChip({
      id: cat.id,
      label: t(cat.i18nKey),
      IconComponent,
      active,
      accentColor: cat.accentColor,
      onPress: () => {
        void selection()
        onSelect(active ? null : cat.id)
      },
      testID: `explore.category-${cat.id}`,
    })
  })

  if (desktop) {
    return (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: SPACING.sm,
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {onFilterPress && (
          <>
            {renderChip({
              id: 'filters-desktop',
              label: t('explore.filter_title'),
              IconComponent: SlidersHorizontal,
              active: activeFilterCount > 0,
              accentColor: CATEGORY_BRAND_PURPLE,
              onPress: () => {
                void selection()
                onFilterPress()
              },
              testID: 'explore.filter-button-desktop',
              badge: activeFilterCount > 0 ? activeFilterCount : undefined,
            })}
            <View
              style={{
                width: 1,
                height: 28,
                backgroundColor: colors.border,
                marginHorizontal: SPACING.xs,
                borderRadius: 1,
                opacity: 0.6,
              }}
            />
          </>
        )}
        {showAllItem}
        {items}
      </View>
    )
  }

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {/* Fixed filter button */}
      {onFilterPress && (
        <>
          <View style={{ paddingLeft: contentPadding, paddingVertical: 4 }}>
            {renderChip({
              id: 'filters-mobile',
              label: t('explore.filter_title'),
              IconComponent: SlidersHorizontal,
              active: activeFilterCount > 0,
              accentColor: CATEGORY_BRAND_PURPLE,
              onPress: () => {
                void selection()
                onFilterPress()
              },
              testID: 'explore.filter-button',
              badge: activeFilterCount > 0 ? activeFilterCount : undefined,
            })}
          </View>
          {/* Vertical separator */}
          <View
            style={{
              width: 1,
              height: 34,
              backgroundColor: colors.border,
              marginHorizontal: SPACING.xs,
              borderRadius: 1,
              opacity: 0.45,
            }}
          />
        </>
      )}
      {/* Scrollable categories */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap: 8,
          paddingRight: contentPadding,
          paddingLeft: onFilterPress ? 0 : contentPadding,
          paddingVertical: 4,
        }}
      >
        {showAllItem}
        {items}
      </ScrollView>
    </View>
  )
}
